import React, { useCallback, useRef } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { EyeOff } from "lucide-react";
import type { TileConfig } from "../types";
import type { WidgetId } from "@/other/constants/EntityManagerConfig";
import { TILE_COLORS } from "../constants";
import { cn } from "@/modern/utils";

interface ConfiguratorTileProps {
  tile: TileConfig;
  columns: number;
  onResize: (id: WidgetId, colSpan: number, rowSpan: number) => void;
  onToggleVisibility: (id: WidgetId) => void;
}

export function ConfiguratorTile({
  tile,
  columns,
  onResize,
  onToggleVisibility,
}: ConfiguratorTileProps) {
  const bgColor = TILE_COLORS[tile.color] ?? TILE_COLORS.blue;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tile.id });

  const style: React.CSSProperties = {
    transform: transform ? CSS.Translate.toString(transform) : undefined,
    transition,
    gridColumn: `span ${Math.min(tile.colSpan, columns)}`,
    gridRow: `span ${tile.rowSpan}`,
    opacity: isDragging ? 0.2 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-configurator-tile
      {...attributes}
      {...listeners}
      className={cn(
        "relative group rounded-md overflow-hidden select-none touch-none",
        "hover:ring-1 hover:ring-white/30 transition-shadow",
        isDragging ? "cursor-grabbing" : "cursor-grab"
      )}
    >
      {/* Colored background */}
      <div
        className="absolute inset-0 rounded-md"
        style={{ backgroundColor: bgColor, opacity: 0.72 }}
      />

      {/* Title */}
      <div className="relative h-full min-h-[100px] flex items-center justify-center px-4">
        <h3 className="text-sm font-medium text-white/90 tracking-wide">
          {tile.title}
        </h3>
      </div>

      {/* Hide button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleVisibility(tile.id);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-80 transition-opacity p-1.5 rounded hover:bg-black/20 z-20"
      >
        <EyeOff className="w-4 h-4 text-white/70" />
      </button>

      {/* Corner resize handle indicator (bottom-right) */}
      <div className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-70 transition-opacity pointer-events-none">
        <svg viewBox="0 0 10 10" className="w-2.5 h-2.5 text-white/60">
          <path d="M9 9L5 9M9 9L9 5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </svg>
      </div>

      {/* === Resize handles: all 4 edges + all 4 corners === */}

      {/* Top edge */}
      <ResizeHandle
        tile={tile}
        columns={columns}
        onResize={onResize}
        axis="vertical"
        direction="negative"
        className="absolute top-0 left-3 right-3 h-1.5 cursor-n-resize rounded-b"
      />

      {/* Bottom edge */}
      <ResizeHandle
        tile={tile}
        columns={columns}
        onResize={onResize}
        axis="vertical"
        direction="positive"
        className="absolute bottom-0 left-3 right-3 h-1.5 cursor-s-resize rounded-t"
      />

      {/* Left edge */}
      <ResizeHandle
        tile={tile}
        columns={columns}
        onResize={onResize}
        axis="horizontal"
        direction="negative"
        className="absolute left-0 top-3 bottom-3 w-1.5 cursor-w-resize rounded-r"
      />

      {/* Right edge */}
      <ResizeHandle
        tile={tile}
        columns={columns}
        onResize={onResize}
        axis="horizontal"
        direction="positive"
        className="absolute right-0 top-3 bottom-3 w-1.5 cursor-e-resize rounded-l"
      />

      {/* Top-left corner */}
      <ResizeHandle
        tile={tile}
        columns={columns}
        onResize={onResize}
        axis="both"
        direction="negative"
        className="absolute top-0 left-0 w-3 h-3 cursor-nw-resize rounded-br"
      />

      {/* Top-right corner */}
      <ResizeHandle
        tile={tile}
        columns={columns}
        onResize={onResize}
        axis="both"
        directionX="positive"
        directionY="negative"
        className="absolute top-0 right-0 w-3 h-3 cursor-ne-resize rounded-bl"
      />

      {/* Bottom-left corner */}
      <ResizeHandle
        tile={tile}
        columns={columns}
        onResize={onResize}
        axis="both"
        directionX="negative"
        directionY="positive"
        className="absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize rounded-tr"
      />

      {/* Bottom-right corner */}
      <ResizeHandle
        tile={tile}
        columns={columns}
        onResize={onResize}
        axis="both"
        direction="positive"
        className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize rounded-tl"
      />
    </div>
  );
}

// --- Resize Handle Component ---

interface ResizeHandleProps {
  tile: TileConfig;
  columns: number;
  onResize: (id: WidgetId, colSpan: number, rowSpan: number) => void;
  axis: "horizontal" | "vertical" | "both";
  direction?: "positive" | "negative";
  directionX?: "positive" | "negative";
  directionY?: "positive" | "negative";
  className?: string;
}

function ResizeHandle({
  tile,
  columns,
  onResize,
  axis,
  direction = "positive",
  directionX,
  directionY,
  className,
}: ResizeHandleProps) {
  const resizeRef = useRef<{
    startX: number;
    startY: number;
    startColSpan: number;
    startRowSpan: number;
    cellW: number;
    cellH: number;
  } | null>(null);

  const xDir = directionX ?? direction;
  const yDir = directionY ?? direction;
  const xSign = xDir === "positive" ? 1 : -1;
  const ySign = yDir === "positive" ? 1 : -1;

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const gridEl = (e.currentTarget as HTMLElement).closest("[data-grid]");
      if (!gridEl) return;

      const gridRect = gridEl.getBoundingClientRect();
      const gap = 8;
      const cellW = (gridRect.width - gap * (columns - 1)) / columns;
      const cellH = 100;

      resizeRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startColSpan: tile.colSpan,
        startRowSpan: tile.rowSpan,
        cellW,
        cellH,
      };

      const handleMouseMove = (ev: MouseEvent) => {
        if (!resizeRef.current) return;
        const { startX, startY, startColSpan, startRowSpan, cellW: cw, cellH: ch } = resizeRef.current;

        let newColSpan = startColSpan;
        let newRowSpan = startRowSpan;

        if (axis === "horizontal" || axis === "both") {
          const dx = (ev.clientX - startX) * xSign;
          newColSpan = Math.max(1, Math.min(columns, Math.round(startColSpan + dx / (cw + gap))));
        }

        if (axis === "vertical" || axis === "both") {
          const dy = (ev.clientY - startY) * ySign;
          newRowSpan = Math.max(1, Math.min(3, Math.round(startRowSpan + dy / (ch + gap))));
        }

        onResize(tile.id, newColSpan, newRowSpan);
      };

      const handleMouseUp = () => {
        resizeRef.current = null;
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [tile.id, tile.colSpan, tile.rowSpan, columns, onResize, axis, xSign, ySign]
  );

  return (
    <div
      onMouseDown={handleMouseDown}
      onPointerDown={(e) => e.stopPropagation()}
      className={cn(
        "opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/25 z-10",
        className
      )}
    />
  );
}
